import { createContext, useContext, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

export interface PageConfig {
  key: string;
  label: string;
  isVisible: boolean;
  heroImageUrl?: string | null;
  updatedAt: string;
}

interface SiteConfigContextValue {
  pages: PageConfig[];
  isVisible: (key: string) => boolean;
  getHeroImage: (key: string) => string | null | undefined;
  loading: boolean;
  refresh: () => void;
}

// default: everything visible (graceful before load)
const SiteConfigContext = createContext<SiteConfigContextValue>({
  pages: [],
  isVisible: () => true,
  getHeroImage: () => undefined,
  loading: true,
  refresh: () => {},
});

export function SiteConfigProvider({ children }: { children: React.ReactNode }) {
  const [pages, setPages] = useState<PageConfig[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = () => {
    apiFetch<PageConfig[]>('/api/site-config/pages')
      .then((data) => { setPages(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, []);

  const isVisible = (key: string) => {
    const cfg = pages.find((p) => p.key === key);
    // if config not loaded yet or key not found, show by default
    return cfg ? cfg.isVisible : true;
  };

  const getHeroImage = (key: string): string | null | undefined => {
    const cfg = pages.find((p) => p.key === key);
    return cfg?.heroImageUrl;
  };

  return (
    <SiteConfigContext.Provider value={{ pages, isVisible, getHeroImage, loading, refresh: fetch }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export const useSiteConfig = () => useContext(SiteConfigContext);
